/**
 * Offline Service — RaconWorks Smart Pre-Cache
 * 
 * Strategy: Cache only assigned permits for the current user.
 * Mutations go into a sync queue and flush on reconnect.
 * 
 * IndexedDB Stores:
 *   - permits:   cached permit documents (keyed by id)
 *   - syncQueue: pending mutations (keyed by auto-increment)
 *   - meta:      last sync timestamp, user id
 */

import { openDB } from 'idb';

const DB_NAME = 'raconworks-offline';
const DB_VERSION = 1;

// --- Database Setup ---

let dbPromise = null;

const getDB = () => {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                // Cached permits store
                if (!db.objectStoreNames.contains('permits')) {
                    db.createObjectStore('permits', { keyPath: 'id' });
                }
                // Sync queue for offline mutations
                if (!db.objectStoreNames.contains('syncQueue')) {
                    db.createObjectStore('syncQueue', { keyPath: 'queueId', autoIncrement: true });
                }
                // Metadata (last sync, current user)
                if (!db.objectStoreNames.contains('meta')) {
                    db.createObjectStore('meta');
                }
            }
        });
    }
    return dbPromise;
};

// --- Permit Cache ---

/**
 * Cache permits to IndexedDB (replaces all cached permits for this user)
 */
export const cachePermits = async (permits) => {
    const db = await getDB();
    const tx = db.transaction('permits', 'readwrite');
    const store = tx.objectStore('permits');

    // Clear old cache
    await store.clear();

    // Write new permits
    for (const permit of permits) {
        await store.put(permit);
    }

    await tx.done;

    // Update last sync time
    await setMeta('lastSync', new Date().toISOString());

    console.log(`[OfflineService] Cached ${permits.length} permits`);
};

/**
 * Get all cached permits from IndexedDB
 */
export const getCachedPermits = async () => {
    const db = await getDB();
    return db.getAll('permits');
};

/**
 * Get a single cached permit by ID
 */
export const getCachedPermit = async (id) => {
    const db = await getDB();
    return db.get('permits', id);
};

/**
 * Update a single cached permit locally (for optimistic UI)
 */
export const updateCachedPermit = async (id, updates) => {
    const db = await getDB();
    const existing = await db.get('permits', id);
    if (existing) {
        const updated = { ...existing, ...updates };
        await db.put('permits', updated);
        return updated;
    }
    return null;
};

// --- Sync Queue ---

/**
 * Add a mutation to the sync queue (for offline writes)
 * 
 * @param {string} action - The permitService method name (e.g., 'updatePermitChecklist')
 * @param {Array} args - The arguments to pass to the method
 */
export const addToSyncQueue = async (action, args) => {
    const db = await getDB();
    await db.add('syncQueue', {
        action,
        args,
        createdAt: new Date().toISOString(),
        retries: 0
    });
    console.log(`[OfflineService] Queued: ${action}`);
};

/**
 * Get all pending sync items
 */
export const getSyncQueue = async () => {
    const db = await getDB();
    return db.getAll('syncQueue');
};

/**
 * Remove a sync item after successful processing
 */
export const removeSyncItem = async (queueId) => {
    const db = await getDB();
    await db.delete('syncQueue', queueId);
};

/**
 * Get pending sync queue count
 */
export const getSyncQueueCount = async () => {
    const db = await getDB();
    return db.count('syncQueue');
};

/**
 * Flush the sync queue — replay all pending mutations via permitService
 * Returns { success: number, failed: number }
 */
export const flushSyncQueue = async (permitService) => {
    const queue = await getSyncQueue();
    if (queue.length === 0) return { success: 0, failed: 0 };

    console.log(`[OfflineService] Flushing ${queue.length} queued items...`);

    let success = 0;
    let failed = 0;

    for (const item of queue) {
        try {
            const method = permitService[item.action];
            if (typeof method === 'function') {
                await method.apply(permitService, item.args);
                await removeSyncItem(item.queueId);
                success++;
                console.log(`[OfflineService] Synced: ${item.action}`);
            } else {
                console.error(`[OfflineService] Unknown action: ${item.action}`);
                // Mark as failed but don't retry unknown actions
                await removeSyncItem(item.queueId);
                failed++;
            }
        } catch (err) {
            console.error(`[OfflineService] Sync failed for ${item.action}:`, err);
            // Increment retry counter, keep in queue for next attempt
            const db = await getDB();
            await db.put('syncQueue', { ...item, retries: (item.retries || 0) + 1 });
            failed++;

            // Abandon after 5 retries
            if ((item.retries || 0) >= 5) {
                console.error(`[OfflineService] Abandoned after 5 retries: ${item.action}`);
                await removeSyncItem(item.queueId);
            }
        }
    }

    return { success, failed };
};

// --- Metadata ---

export const setMeta = async (key, value) => {
    const db = await getDB();
    await db.put('meta', value, key);
};

export const getMeta = async (key) => {
    const db = await getDB();
    return db.get('meta', key);
};

// --- Online/Offline Detection ---

/**
 * Check if the browser is currently online
 */
export const isOnline = () => {
    return navigator.onLine;
};

/**
 * Register callbacks for online/offline state changes
 * Returns a cleanup function
 */
export const onConnectivityChange = (onOnline, onOffline) => {
    const handleOnline = () => {
        console.log('[OfflineService] Back online');
        if (onOnline) onOnline();
    };
    const handleOffline = () => {
        console.log('[OfflineService] Went offline');
        if (onOffline) onOffline();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
    };
};

// --- Smart Pre-Cache Hook ---

/**
 * Pre-cache permits for the given user.
 * Call this on login or when monteur opens their dashboard.
 */
export const preCacheForUser = async (userId, fetchPermits) => {
    if (!isOnline()) {
        console.log('[OfflineService] Offline, skipping pre-cache');
        return;
    }

    try {
        const permits = await fetchPermits(userId);
        await cachePermits(permits);
        await setMeta('cachedUserId', userId);
        return permits.length;
    } catch (err) {
        console.error('[OfflineService] Pre-cache failed:', err);
        return 0;
    }
};

/**
 * Clear all offline data (e.g., on logout)
 */
export const clearOfflineData = async () => {
    const db = await getDB();
    const tx = db.transaction(['permits', 'syncQueue', 'meta'], 'readwrite');
    await tx.objectStore('permits').clear();
    await tx.objectStore('syncQueue').clear();
    await tx.objectStore('meta').clear();
    await tx.done;
    console.log('[OfflineService] All offline data cleared');
};

export default {
    cachePermits,
    getCachedPermits,
    getCachedPermit,
    updateCachedPermit,
    addToSyncQueue,
    getSyncQueue,
    removeSyncItem,
    getSyncQueueCount,
    flushSyncQueue,
    setMeta,
    getMeta,
    isOnline,
    onConnectivityChange,
    preCacheForUser,
    clearOfflineData
};
